import { mapState, mapGetters, mapActions } from 'pinia'
import { useUserStore } from './user'
import { useAppStore } from './app'
import { useWwStore } from './ww'

// Store map for easy lookup
const storeMap = {
  user: useUserStore,
  app: useAppStore,
  ww: useWwStore
}

/**
 * Helper to create two-way computed properties for Pinia store state
 * Replaces vuex-map-fields functionality
 *
 * Usage:
 *   computed: {
 *     ...mapFields('app', ['loginDialog', 'drawer'])
 *   }
 *
 * Or with nested path:
 *   computed: {
 *     ...mapFields({ loginDialog: 'app.loginDialog' })
 *   }
 */
export function mapFields(storeIdOrMapping, fields) {
  const result = {}

  if (typeof storeIdOrMapping === 'string') {
    // Format: mapFields('app', ['loginDialog', 'drawer'])
    const storeId = storeIdOrMapping
    const useStore = storeMap[storeId]

    if (!useStore) {
      console.warn(`Store "${storeId}" not found`)
      return result
    }

    fields.forEach(field => {
      result[field] = {
        get() {
          const store = useStore()
          return store[field]
        },
        set(value) {
          const store = useStore()
          store[field] = value
        }
      }
    })
  } else {
    // Format: mapFields({ loginDialog: 'app.loginDialog' })
    const mapping = storeIdOrMapping
    Object.entries(mapping).forEach(([key, path]) => {
      const [storeId, field] = path.split('.')
      const useStore = storeMap[storeId]

      if (!useStore) {
        console.warn(`Store "${storeId}" not found`)
        return
      }

      result[key] = {
        get() {
          const store = useStore()
          return store[field]
        },
        set(value) {
          const store = useStore()
          store[field] = value
        }
      }
    })
  }

  return result
}

/**
 * Maps Pinia store state to computed properties (read-only)
 * Compatible with Vuex mapState signature
 *
 * Usage:
 *   ...mapStoreState('user', ['user', 'groups', 'emailAlerts'])
 *   ...mapStoreState({ watchlists: state => state.user.watchlists })
 */
export function mapStoreState(storeIdOrMapping, fields) {
  if (typeof storeIdOrMapping === 'string') {
    const useStore = storeMap[storeIdOrMapping]
    if (!useStore) {
      console.warn(`Store "${storeIdOrMapping}" not found`)
      return {}
    }
    return mapState(useStore, fields)
  } else {
    // Object mapping format: { watchlists: state => state.user.watchlists }
    const result = {}
    Object.entries(storeIdOrMapping).forEach(([key, accessor]) => {
      if (typeof accessor === 'function') {
        // Extract store name from the function by analyzing what it accesses
        // For now, support explicit store.field format
        result[key] = function () {
          // Try to figure out which store this is accessing
          const userStore = useUserStore()
          const appStore = useAppStore()
          const wwStore = useWwStore()

          // Create a proxy object that has all store states
          const combinedState = {
            user: userStore.$state,
            app: appStore.$state,
            ww: wwStore.$state
          }
          return accessor(combinedState)
        }
      }
    })
    return result
  }
}

/**
 * Maps Pinia store getters to computed properties
 * Compatible with Vuex mapGetters signature
 *
 * Usage:
 *   ...mapStoreGetters('user', ['subscription', 'isLoggedIn'])
 *   ...mapStoreGetters({ subscription: 'user/subscription' })
 */
export function mapStoreGetters(storeIdOrMapping, getters) {
  if (typeof storeIdOrMapping === 'string') {
    const useStore = storeMap[storeIdOrMapping]
    if (!useStore) {
      console.warn(`Store "${storeIdOrMapping}" not found`)
      return {}
    }
    return mapGetters(useStore, getters)
  } else {
    // Object mapping format: { subscription: 'user/subscription' }
    const result = {}
    Object.entries(storeIdOrMapping).forEach(([key, path]) => {
      const [storeId, getter] = path.split('/')
      const useStore = storeMap[storeId]
      if (!useStore) {
        console.warn(`Store "${storeId}" not found`)
        return
      }
      const mapped = mapGetters(useStore, [getter])
      result[key] = mapped[getter]
    })
    return result
  }
}

/**
 * Maps Pinia store actions to methods
 *
 * Usage:
 *   ...mapStoreActions('user', ['loadUser', 'loadGroups'])
 */
export function mapStoreActions(storeId, actions) {
  const useStore = storeMap[storeId]
  if (!useStore) {
    console.warn(`Store "${storeId}" not found`)
    return {}
  }
  return mapActions(useStore, actions)
}

// Re-export for convenience
export { useUserStore, useAppStore, useWwStore }
